import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const TitlesForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null); // Track which title is being deleted
  const [institutes, setInstitutes] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [titles, setTitles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  // Form data for new title
  const [formData, setFormData] = useState({
    type: 'training', // Default to training
    schemeId: '',
    code: '',
    name: '',
    SIR: false, // Checkbox for State Institute
    RIR: false, // Checkbox for Regional Institute
    DRC: false,  // Checkbox for District Institutes
    title_types: [] // Array for title types
  });
  
  // New title type input value
  const [newTitleType, setNewTitleType] = useState('');
  
  // Selected institute based on user role (will be set automatically)
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  
  // Get user role from localStorage
  const getUserRole = () => {
    try {
      return localStorage.getItem('userRole') || '';
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return '';
    }
  };
  
  // Determine the appropriate institute code based on user role
  const getInstituteCode = () => {
    const userRole = getUserRole();
    
    if (userRole === 'state') {
      return 'SIR';
    } else if (userRole === 'region') {
      return 'RIR';
    } else if (userRole === 'district') {
      return 'DRC';
    }
    
    return '';
  };
  
  // Fetch initial data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get institute data using the API service
        const institutesData = await api.master.getInstitutes();
        
        if (institutesData && !institutesData.error) {
          setInstitutes(institutesData.data);
          
          // Set selected institute based on user role
          const instituteCode = getInstituteCode();
          const institute = institutesData.data.find(
            inst => inst.code === instituteCode || 
              (instituteCode === 'SIR' && inst.name === 'SIRD') ||
              (instituteCode === 'RIR' && inst.name === 'RIRD') ||
              (instituteCode === 'DRC' && inst.name === 'DRCP')
          );
          
          if (institute) {
            setSelectedInstitute(institute);
          }
        }
        
        // Fetch initial schemes (training by default)
        await fetchSchemes('training');
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Failed to load data. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Fetch schemes based on type (training, infrastructure, or admin)
  const fetchSchemes = async (type) => {
    setLoading(true);
    try {
      const response = await fetch(`${api.BASE_URL}/master/scheme/${type}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to load ${type} schemes`);
      }
      
      const data = await response.json();
      
      if (data && !data.error) {
        setSchemes(data.data || []);
        
        // Reset scheme selection and titles when changing type
        setFormData(prev => ({
          ...prev,
          type,
          schemeId: '',
          SIR: false,
          RIR: false,
          DRC: false,
          title_types: []
        }));
        setTitles([]);
      }
    } catch (error) {
      console.error(`Error fetching ${type} schemes:`, error);
      alert(`Failed to load ${type} schemes. Please check your connection and try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch titles based on selected scheme
  const fetchTitles = async (schemeId, type) => {
    if (!schemeId) {
      setTitles([]);
      return;
    }
    
    setLoading(true);
    try {
      let endpoint = '';
      if (type === 'training') {
        endpoint = `${api.BASE_URL}/master/title/training/${schemeId}`;
      } else if (type === 'infra') {
        endpoint = `${api.BASE_URL}/master/title/infra/${schemeId}`;
      } else if (type === 'admin') {
        endpoint = `${api.BASE_URL}/master/title/admin/${schemeId}`;
      }
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to load ${type} titles`);
      }
      
      const data = await response.json();
      
      if (data && !data.error) {
        setTitles(data.data || []);
      }
    } catch (error) {
      console.error(`Error fetching ${type} titles:`, error);
      alert(`Failed to load ${type} titles. Please check your connection and try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete title
  const handleDeleteTitle = async (titleId) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this title? This action cannot be undone.')) {
      return;
    }
    
    setDeleteLoading(titleId);
    try {
      const response = await fetch(`${api.BASE_URL}/master/title/${titleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify({ status: 'Delete' })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete title');
      }
      
      const data = await response.json();
      
      if (data && !data.error) {
        alert('Title deleted successfully!');
        // Refresh titles list
        fetchTitles(formData.schemeId, formData.type);
      } else {
        throw new Error(data.message || 'Failed to delete title');
      }
    } catch (error) {
      console.error('Error deleting title:', error);
      alert(`Failed to delete title: ${error.message}`);
    } finally {
      setDeleteLoading(null);
    }
  };
  
  // Handle input changes for form fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked ? 1 : 0 // Convert boolean to 1/0 for API
      });
    } else {
      // Special handling for type - fetch schemes when type changes
      if (name === 'type') {
        fetchSchemes(value);
      }
      
      // Special handling for schemeId - fetch titles when scheme changes
      if (name === 'schemeId') {
        fetchTitles(value, formData.type);
      }
      
      // Special handling for code to uppercase and limit to 3 characters
      if (name === 'code') {
        const uppercaseValue = value.toUpperCase();
        if (uppercaseValue.length <= 3) {
          setFormData({
            ...formData,
            [name]: uppercaseValue
          });
        }
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    }
  };
  
  // Handle adding a new title type
  const handleAddTitleType = (e) => {
    e.preventDefault();
    
    if (!newTitleType.trim()) {
      return; // Don't add empty title types
    }
    
    setFormData(prevData => ({
      ...prevData,
      title_types: [...prevData.title_types, newTitleType.trim()]
    }));
    
    setNewTitleType(''); // Clear the input
  };
  
  // Handle removing a title type
  const handleRemoveTitleType = (index) => {
    setFormData(prevData => ({
      ...prevData,
      title_types: prevData.title_types.filter((_, i) => i !== index)
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.schemeId || !formData.code || !formData.name) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (formData.code.length !== 3) {
      alert('Title code must be exactly 3 characters');
      return;
    }
    
    if (!formData.SIR && !formData.RIR && !formData.DRC) {
      alert('Please select at least one institute');
      return;
    }
    
    setLoading(true);
    try {
      const requestBody = {
        p_id: formData.schemeId, // Use p_id as specified in the API
        type: formData.type,
        code: formData.code,
        name: formData.name,
        SIR: formData.SIR,
        RIR: formData.RIR,
        DRC: formData.DRC
      };
      
      // Only include title_types if there are any
      if (formData.title_types.length > 0) {
        requestBody.title_types = formData.title_types;
      }
      
      const response = await fetch(`${api.BASE_URL}/master/title`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add title');
      }
      
      const data = await response.json();
      
      if (data && !data.error) {
        alert('Title added successfully!');
        // Reset form and refresh titles list
        setFormData({
          ...formData,
          code: '',
          name: '',
          SIR: false,
          RIR: false,
          DRC: false,
          title_types: []
        });
        fetchTitles(formData.schemeId, formData.type);
        setShowForm(false);
      } else {
        throw new Error(data.message || 'Failed to add title');
      }
    } catch (error) {
      console.error('Error adding title:', error);
      alert(`Failed to add title: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
  };
  
  return (
    <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-4xl mx-auto border border-gray-100">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b pb-6">
          <div>
            <h2 className="text-2xl font-bold text-blue-800 mb-2">Titles Management</h2>
            <p className="text-gray-600">Manage training, infrastructure, and admin titles</p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button
              onClick={toggleForm}
              className={`px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors ${
                showForm 
                  ? 'bg-gray-200 text-gray-800 border border-gray-300' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {showForm ? 'Cancel' : 'Add New Title'}
            </button>
          </div>
        </div>
        
        {/* Loading indicator */}
        {loading && (
          <div className="text-center mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-blue-700 font-medium">Loading data, please wait...</p>
          </div>
        )}
        
        {/* Type of Work Selector (always visible) */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type of Work
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="type"
                value="training"
                checked={formData.type === 'training'}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Training</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="type"
                value="infra"
                checked={formData.type === 'infra'}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Infrastructure</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="type"
                value="admin"
                checked={formData.type === 'admin'}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">Admin</span>
            </label>
          </div>
        </div>
        
        {/* Show institute information */}
        {selectedInstitute && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Your Institute</h3>
            <p className="text-lg font-semibold text-gray-800">{selectedInstitute.name}</p>
            <p className="text-sm text-gray-600">Code: {selectedInstitute.code}</p>
          </div>
        )}
        
        {/* Scheme Selection (always visible) */}
        <div className="mb-8">
          <label htmlFor="schemeId" className="block text-sm font-medium text-gray-700 mb-1">
            Select Scheme
          </label>
          <select
            id="schemeId"
            name="schemeId"
            value={formData.schemeId}
            onChange={handleChange}
            className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
          >
            <option value="">-- Select a scheme --</option>
            {schemes.map(scheme => (
              <option key={scheme.id} value={scheme.id}>
                {scheme.code} - {scheme.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Add New Title Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Add New {formData.type === 'training' ? 'Training' : formData.type === 'infra' ? 'Infrastructure' : 'Admin'} Title
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Title Code */}
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Title Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="3-letter code (e.g. ABC)"
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                  required
                  maxLength={3}
                  disabled={!formData.schemeId}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter a unique 3-letter code for the title
                </p>
              </div>
              
              {/* Title Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Title Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter title name"
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                  required
                  disabled={!formData.schemeId}
                />
              </div>
            </div>
            
            {/* Institute Checkboxes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Institutes <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="SIR"
                    checked={formData.SIR === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">State Institute (SIRD)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="RIR"
                    checked={formData.RIR === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">Regional Institute (RIRD)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="DRC"
                    checked={formData.DRC === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">District Resource Centers (DRCP)</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Select which institutes can use this title
              </p>
            </div>
            
            {/* Title Types (visible only for admin type) */}
            {formData.type === 'admin' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title Types <span className="text-red-500">*</span>
                </label>
                
                <div className="mb-4">
                  <div className="flex">
                    <input
                      type="text"
                      value={newTitleType}
                      onChange={(e) => setNewTitleType(e.target.value)}
                      placeholder="Enter a title type"
                      className="flex-grow rounded-l-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                      disabled={!formData.schemeId}
                    />
                    <button
                      type="button"
                      onClick={handleAddTitleType}
                      disabled={!formData.schemeId || !newTitleType.trim()}
                      className="rounded-r-md border border-l-0 border-blue-600 bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Add specific types or categories for this admin title (e.g., "DRCP Salary", "DRCP Assistant Salary")
                  </p>
                </div>
                
                {/* Display added title types */}
                {formData.title_types.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Added Title Types:</h4>
                    <ul className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200">
                      {formData.title_types.map((type, index) => (
                        <li key={index} className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm text-gray-700">{type}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTitleType(index)}
                            className="text-red-600 hover:text-red-800 focus:outline-none"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {formData.title_types.length === 0 && (
                  <div className="text-amber-600 text-sm mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Admin titles require at least one title type
                  </div>
                )}
              </div>
            )}
            
            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading || !formData.schemeId || (formData.type === 'admin' && formData.title_types.length === 0)}
                className="px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add Title'}
              </button>
            </div>
          </form>
        )}
        
        {/* View Titles Table - Always visible but dependent on a scheme being selected */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {formData.type === 'training' 
              ? 'Training' 
              : formData.type === 'infra' 
                ? 'Infrastructure' 
                : 'Admin'} Titles
          </h3>
          
          {!formData.schemeId ? (
            <div className="text-center py-8 text-gray-500">
              Please select a scheme to view titles
            </div>
          ) : titles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      State (SIRD)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Regional (RIRD)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      District (DRCP)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title Types
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {titles.map((title) => (
                    <tr key={title.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {title.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {title.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {title.SIR === 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {title.RIR === 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {title.DRC === 1 ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {title.title_types && title.title_types.length > 0 ? (
                          <div className="max-w-xs">
                            <div className="flex flex-wrap gap-1">
                              {title.title_types.map((type, typeIndex) => (
                                <span key={typeIndex} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-100">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">No types</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleDeleteTitle(title.id)}
                          className="text-red-600 hover:text-red-900 focus:outline-none"
                          disabled={deleteLoading === title.id}
                        >
                          {deleteLoading === title.id ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Deleting...
                            </span>
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No titles found for this scheme. Add a new title to get started.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TitlesForm;